import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DashboardProject } from '../project/types/project';
import {
  clearWeldAnalytics,
  fetchWeldAnalyticsBundle,
  fetchWeldAnalyticsProduction,
  fetchWeldHealth,
  triggerWeldPipeline,
} from '../../redux/WeldAnalytics/actions';
import {
  getStoredSelection,
  jobRefsForPipes,
  jobRefsFromProductionRows,
  jobRefsKey,
  mergeJobRefs,
  pruneJobRefs,
  setStoredSelection,
  uniqueJobNumbersFromRefs,
  uniquePipeIdsFromRefs,
  type PipeJobRef,
} from './utils/jobNumbersFromSetup';
import { useCurrentProject } from '../../redux/WeldData/hooks/useWeldData';
import type { PipeOption } from '../ndt/components/FilterPanel';
import { WeldDetailView, type WeldDetailExtras } from './components/WeldDetailView';
import type { WeldFilterSelection } from './components/WeldFilterPanel';
import type { WeldAnalyticsFilters } from './types/weld';
import type { PendingWeldDrillDown } from './components/dashboard/drilldown/drillDownTypes';
import { Notification } from '../../components/ui/custom-notification';
import {
  allPartialErrorsArePipelineUnavailable,
  WELDIQ_SOME_JOBS_NOT_READY,
} from './utils/weldIqEmptyState';

interface WeldDashboardProps {
  project: DashboardProject;
  pipeIds?: string[];
  pipeOptions?: PipeOption[];
  onPipeIdsChange?: (pipeIds: string[]) => void;
  /** @deprecated use pipeIds */
  pipeId?: string | null;
  /** @deprecated use onPipeIdsChange */
  onPipeChange?: (pipeId: string | null) => void;
  /** Open a weld drill-down from Combined (or other hosts). */
  externalPendingDrillDown?: PendingWeldDrillDown | null;
  onExternalPendingDrillDownConsumed?: () => void;
  /** Combined-only NDT blocks. Weld Analytics does not pass these. */
  detailExtras?: WeldDetailExtras;
  /** Controlled Filters panel open state (from AnalyticsShell). */
  filtersOpen?: boolean;
  onFiltersOpenChange?: (open: boolean) => void;
}

const emptyFilters = (): WeldFilterSelection => ({
  jobRefs: [],
  weldingSystem: '',
  passFamily: '',
  anomalyOnly: false,
  dateFrom: '',
  dateTo: '',
});

function toApiFilters(sel: WeldFilterSelection): WeldAnalyticsFilters {
  return {
    jobNumbers: uniqueJobNumbersFromRefs(sel.jobRefs),
    weldingSystem: sel.weldingSystem || null,
    passFamily: sel.passFamily || null,
    anomalyOnly: sel.anomalyOnly,
    dateFrom: sel.dateFrom || null,
    dateTo: sel.dateTo || null,
  };
}

function isWeldAdmin(groups: string[] | undefined): boolean {
  if (!groups?.length) return false;
  return groups.some(
    (g) =>
      g === 'super-admin' ||
      g.includes('weldiq-prod-group-admin') ||
      g === 'admin' ||
      (g.includes('weldiq') && g.includes('admin')),
  );
}

function pipeIdsKey(ids: string[]): string {
  return [...ids].map(String).sort().join('|');
}

export const WeldDashboard = ({
  project,
  pipeIds: pipeIdsProp,
  pipeOptions = [],
  onPipeIdsChange,
  pipeId = null,
  onPipeChange,
  externalPendingDrillDown = null,
  onExternalPendingDrillDownConsumed,
  detailExtras,
  filtersOpen = false,
  onFiltersOpenChange,
}: WeldDashboardProps) => {
  const dispatch = useDispatch();
  const projectId = project?.project?.projectId;
  const projectName = project?.project?.projectName;
  const requestIdRef = useRef(0);
  const lastPartialToastRef = useRef<string>('');
  const restoredSelectionRef = useRef(false);
  const initialAutoLoadDoneRef = useRef(false);
  const productionDiscoverKeyRef = useRef<string>('');
  const prevProjectIdRef = useRef<string | number | undefined>(undefined);

  const pipeIds = useMemo(() => {
    if (Array.isArray(pipeIdsProp) && pipeIdsProp.length > 0) {
      return pipeIdsProp.map(String);
    }
    if (pipeId != null && pipeId !== '') return [String(pipeId)];
    return [];
  }, [pipeIdsProp, pipeId]);

  const {
    bundle,
    health,
    isFetching,
    error,
    isTriggeringPipeline,
    pipelineResult,
    pipelineError,
    productionRows,
    productionLoading,
    productionError,
    productionPartialErrors,
    statusCalculationMethod,
  } = useSelector((state: { weldAnalytics?: Record<string, unknown> }) => state.weldAnalytics || {});

  const pipeDetails = useSelector(
    (state: { setupData?: { pipeDetails?: Record<string, unknown> } }) =>
      state.setupData?.pipeDetails,
  );
  const authGroups = useSelector(
    (state: { auth?: { user?: { groups?: string[] } } }) => state.auth?.user?.groups,
  );

  const [filterSelection, setFilterSelection] = useState<WeldFilterSelection>(emptyFilters);
  const [lastAppliedJobRefs, setLastAppliedJobRefs] = useState<PipeJobRef[]>([]);
  const lastPipeIdsKeyRef = useRef<string>('');

  const { setProject, currentProject } = useCurrentProject();

  const setupJobRefs = useMemo(
    () => jobRefsForPipes(pipeDetails, pipeIds),
    [pipeDetails, pipeIds],
  );

  const productionJobRefs = useMemo(
    () =>
      jobRefsFromProductionRows(
        (productionRows as { job_number?: string; jobNumber?: string; pipeId?: string | number }[]) ||
          [],
        pipeIds,
      ),
    [productionRows, pipeIds],
  );

  /** Setup jobs preferred; production rows fill gaps when Setup has none (e.g. Targa). */
  const availableJobRefs = useMemo(
    () => mergeJobRefs(setupJobRefs, productionJobRefs),
    [setupJobRefs, productionJobRefs],
  );

  const jobOptions = useMemo(() => {
    const labelByPipe = new Map(
      pipeOptions.map((p) => [String(p.pipeId), p.displayName || String(p.pipeId)]),
    );
    return availableJobRefs.map((ref) => ({
      value: `${ref.pipeId}::${ref.jobNumber}`,
      label: ref.jobNumber,
      group: labelByPipe.get(ref.pipeId) || ref.pipeId,
    }));
  }, [availableJobRefs, pipeOptions]);

  useEffect(() => {
    if (project?.project?.projectId && project.project.projectId !== currentProject?.project?.projectId) {
      setProject(project);
    }
  }, [project, currentProject, setProject]);

  // Health on project enter; clear analytics only when project changes (not tab unmount).
  useEffect(() => {
    if (projectId) {
      dispatch(fetchWeldHealth());
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (
      prevProjectIdRef.current != null &&
      prevProjectIdRef.current !== projectId
    ) {
      dispatch(clearWeldAnalytics());
      restoredSelectionRef.current = false;
      initialAutoLoadDoneRef.current = false;
      productionDiscoverKeyRef.current = '';
      lastPipeIdsKeyRef.current = '';
    }
    prevProjectIdRef.current = projectId;
  }, [dispatch, projectId]);

  // Restore last applied filter selection for this project (sessionStorage).
  useEffect(() => {
    if (!projectId || restoredSelectionRef.current || pipeIds.length === 0) return;
    const stored = getStoredSelection(projectId);
    if (!stored?.jobRefs?.length) {
      restoredSelectionRef.current = true;
      return;
    }
    const pruned = pruneJobRefs(stored.jobRefs, pipeIds);
    if (pruned.length === 0) {
      restoredSelectionRef.current = true;
      return;
    }
    restoredSelectionRef.current = true;
    initialAutoLoadDoneRef.current = true;
    setFilterSelection((prev) => ({ ...prev, jobRefs: pruned }));
    setLastAppliedJobRefs(pruned);
    const requestId = (requestIdRef.current += 1);
    dispatch(
      fetchWeldAnalyticsBundle({
        ...toApiFilters({ ...emptyFilters(), jobRefs: pruned }),
        projectId,
        projectName,
        requestId,
      }),
    );
    dispatch(
      fetchWeldAnalyticsProduction({
        projectId,
        projectName,
        pipeIds: uniquePipeIdsFromRefs(pruned),
        requestId,
      }),
    );
  }, [dispatch, projectId, projectName, pipeIds]);

  const applyRefsAndLoad = useCallback(
    (refs: PipeJobRef[]) => {
      if (!projectId || refs.length === 0) return;
      setFilterSelection({
        ...emptyFilters(),
        jobRefs: refs,
      });
      setLastAppliedJobRefs([...refs]);
      initialAutoLoadDoneRef.current = true;
      setStoredSelection(projectId, {
        pipeIds: uniquePipeIdsFromRefs(refs),
        jobRefs: refs,
      });
      const requestId = (requestIdRef.current += 1);
      dispatch(
        fetchWeldAnalyticsBundle({
          ...toApiFilters({ ...emptyFilters(), jobRefs: refs }),
          projectId,
          projectName,
          requestId,
        }),
      );
      dispatch(
        fetchWeldAnalyticsProduction({
          projectId,
          projectName,
          pipeIds: uniquePipeIdsFromRefs(refs),
          requestId,
        }),
      );
    },
    [dispatch, projectId, projectName],
  );

  const discoverJobsFromProduction = useCallback(
    (ids: string[]) => {
      if (!projectId || ids.length === 0) return;
      const key = pipeIdsKey(ids);
      if (productionDiscoverKeyRef.current === key) return;
      productionDiscoverKeyRef.current = key;
      const requestId = (requestIdRef.current += 1);
      dispatch(
        fetchWeldAnalyticsProduction({
          projectId,
          projectName,
          pipeIds: ids,
          requestId,
        }),
      );
    },
    [dispatch, projectId, projectName],
  );

  // When committed pipes change: sync jobs and auto-load (no Apply required for pipe-only changes).
  useEffect(() => {
    if (!projectId) return;
    const key = pipeIdsKey(pipeIds);
    const refs = jobRefsForPipes(pipeDetails, pipeIds);

    // Same pipe selection, but Setup jobs arrived later (Golphino cold race) → finish auto-load.
    if (lastPipeIdsKeyRef.current === key) {
      if (refs.length === 0) return;
      setFilterSelection((prev) => {
        if (prev.jobRefs.length > 0) return prev;
        return { ...prev, jobRefs: refs };
      });
      if (!initialAutoLoadDoneRef.current) {
        applyRefsAndLoad(refs);
      }
      return;
    }

    const prevKey = lastPipeIdsKeyRef.current;
    lastPipeIdsKeyRef.current = key;
    productionDiscoverKeyRef.current = '';

    // First key may be owned by session restore — skip auto-load until restore settles.
    if (prevKey === '' && !restoredSelectionRef.current) {
      setFilterSelection({
        ...emptyFilters(),
        jobRefs: refs,
      });
      if (refs.length === 0 && pipeIds.length > 0) {
        discoverJobsFromProduction(pipeIds.map(String));
      }
      return;
    }

    if (pipeIds.length === 0) {
      setFilterSelection(emptyFilters());
      setLastAppliedJobRefs([]);
      return;
    }

    // No Setup jobs yet: still fetch production by pipe so we can discover jobs (Targa).
    if (refs.length === 0) {
      setFilterSelection(emptyFilters());
      setLastAppliedJobRefs([]);
      discoverJobsFromProduction(pipeIds.map(String));
      return;
    }

    applyRefsAndLoad(refs);
  }, [
    dispatch,
    projectId,
    projectName,
    pipeIds,
    pipeDetails,
    applyRefsAndLoad,
    discoverJobsFromProduction,
  ]);

  // Cold start / after restore flag: first pipe + jobs load without Apply.
  useEffect(() => {
    if (!projectId || pipeIds.length === 0) return;
    if (!restoredSelectionRef.current || initialAutoLoadDoneRef.current) return;
    if (lastAppliedJobRefs.length > 0) {
      initialAutoLoadDoneRef.current = true;
      return;
    }
    const refs =
      filterSelection.jobRefs.length > 0
        ? filterSelection.jobRefs
        : jobRefsForPipes(pipeDetails, pipeIds);
    // Empty Setup jobs: production-discover effect loads once rows arrive.
    if (refs.length === 0) return;

    applyRefsAndLoad(refs);
  }, [
    dispatch,
    projectId,
    projectName,
    pipeIds,
    pipeDetails,
    filterSelection.jobRefs,
    lastAppliedJobRefs.length,
    applyRefsAndLoad,
  ]);

  // Production-discovered jobs (Setup empty): auto-select + load WeldIQ once rows arrive.
  useEffect(() => {
    if (!projectId || pipeIds.length === 0) return;
    if (initialAutoLoadDoneRef.current || lastAppliedJobRefs.length > 0) return;
    if (setupJobRefs.length > 0) return;
    if (productionJobRefs.length === 0) return;
    applyRefsAndLoad(productionJobRefs);
  }, [
    projectId,
    pipeIds,
    setupJobRefs.length,
    productionJobRefs,
    lastAppliedJobRefs.length,
    applyRefsAndLoad,
  ]);

  const loadBundle = useCallback(() => {
    if (filterSelection.jobRefs.length === 0) return;
    const uniquePipes = uniquePipeIdsFromRefs(filterSelection.jobRefs);

    if (projectId) {
      setStoredSelection(projectId, {
        pipeIds: uniquePipes,
        jobRefs: filterSelection.jobRefs,
      });
    }
    setLastAppliedJobRefs([...filterSelection.jobRefs]);

    const requestId = (requestIdRef.current += 1);
    dispatch(
      fetchWeldAnalyticsBundle({
        ...toApiFilters(filterSelection),
        projectId,
        projectName,
        requestId,
      }),
    );
    if (projectId) {
      dispatch(
        fetchWeldAnalyticsProduction({
          projectId,
          projectName,
          pipeIds: uniquePipes,
          requestId,
        }),
      );
    }
  }, [dispatch, filterSelection, projectId, projectName]);

  // Soft-fail job errors: pipeline/auth gaps use in-dashboard banners only (no raw S3 toasts).
  useEffect(() => {
    const b = bundle as {
      partialErrors?: { jobNumber: string; error: string }[];
      weldIqUnavailable?: string | boolean;
    } | null;
    if (b?.weldIqUnavailable) return;
    const partial = b?.partialErrors;
    if (!partial?.length) return;
    if (allPartialErrorsArePipelineUnavailable(partial)) return;
    const key = `jobs:${partial.map((e) => e.jobNumber).join('|')}`;
    if (lastPartialToastRef.current === key) return;
    lastPartialToastRef.current = key;
    Notification.warning(WELDIQ_SOME_JOBS_NOT_READY);
  }, [bundle]);

  // Soft-fail pipe errors from analytics-owned weld-status snapshot.
  useEffect(() => {
    const partial = productionPartialErrors as
      | { pipeId?: string | null; error: string }[]
      | undefined;
    if (!partial?.length) return;
    const summary = partial
      .map((e) => (e.pipeId ? `pipe ${e.pipeId}` : 'pipe') + `: ${e.error}`)
      .join('; ');
    const key = `pipes:${summary}`;
    if (lastPartialToastRef.current === key) return;
    lastPartialToastRef.current = key;
    Notification.warning(`Some pipes failed to load: ${summary}`);
  }, [productionPartialErrors]);

  const filtersDirty = useMemo(
    () => jobRefsKey(filterSelection.jobRefs) !== jobRefsKey(lastAppliedJobRefs),
    [filterSelection.jobRefs, lastAppliedJobRefs],
  );

  const appliedJobNumbers = useMemo(
    () => uniqueJobNumbersFromRefs(lastAppliedJobRefs),
    [lastAppliedJobRefs],
  );

  const appliedPipeIds = useMemo(
    () => uniquePipeIdsFromRefs(lastAppliedJobRefs),
    [lastAppliedJobRefs],
  );

  const handleApply = () => {
    loadBundle();
  };

  const handleTriggerPipeline = () => {
    dispatch(triggerWeldPipeline({ pipelineType: 'full' }));
  };

  useEffect(() => {
    if (!onPipeChange) return;
    onPipeChange(pipeIds[0] ?? null);
  }, [pipeIds, onPipeChange]);

  void onPipeIdsChange;

  // WeldIQ soft/unavailable must not block Overview when Data360 production may still load.
  const productionBlockingError = (productionError as string | null | undefined) || null;
  const weldIqHardError = (error as string | null | undefined) || null;
  const hasProductionRows =
    Array.isArray(productionRows) && (productionRows as unknown[]).length > 0;
  const overviewError =
    productionBlockingError && !hasProductionRows ? productionBlockingError : null;
  const statisticsError =
    weldIqHardError && !(bundle as { weldIqUnavailable?: unknown } | null)?.weldIqUnavailable
      ? weldIqHardError
      : null;

  const isApplying = Boolean(isFetching || productionLoading);
  const hasAppliedData = lastAppliedJobRefs.length > 0;

  return (
    <WeldDetailView
      bundle={bundle as import('./types/weld').WeldAnalyticsBundle | null}
      filterSelection={filterSelection}
      jobOptions={jobOptions}
      pipeSelected={pipeIds.length > 0}
      onFilterChange={(next) => setFilterSelection((prev) => ({ ...prev, ...next }))}
      onApplyFilters={handleApply}
      filtersDirty={filtersDirty}
      appliedJobNumbers={appliedJobNumbers}
      appliedJobRefs={lastAppliedJobRefs}
      appliedPipeIds={appliedPipeIds}
      isLoading={Boolean(isFetching)}
      isApplying={isApplying}
      hasAppliedData={hasAppliedData}
      error={overviewError}
      weldIqError={statisticsError}
      health={(health as Record<string, unknown>) ?? null}
      isAdmin={isWeldAdmin(authGroups)}
      isTriggeringPipeline={Boolean(isTriggeringPipeline)}
      pipelineResult={(pipelineResult as Record<string, unknown>) ?? null}
      pipelineError={(pipelineError as string) ?? null}
      onTriggerPipeline={handleTriggerPipeline}
      productionWeldData={(productionRows as Record<string, unknown>[]) || []}
      productionWeldDataLoading={Boolean(productionLoading)}
      productionStatusCalculationMethod={
        (statusCalculationMethod as string) || 'Instantaneous'
      }
      projectId={projectId != null ? Number(projectId) : null}
      pipeId={pipeIds[0] ?? null}
      pipeIds={pipeIds}
      pipeOptions={pipeOptions}
      canEditNdt={isWeldAdmin(authGroups)}
      externalPendingDrillDown={externalPendingDrillDown}
      onExternalPendingDrillDownConsumed={onExternalPendingDrillDownConsumed}
      detailExtras={detailExtras}
      filtersOpen={filtersOpen}
      onFiltersOpenChange={onFiltersOpenChange}
    />
  );
};

export default WeldDashboard;
